'use client';
import React, { useState, useEffect, useCallback } from 'react';
import EnrollModal from './EnrollModal';
import PaymentModal from './PaymentModal';

// Helper function to convert card image URLs to hero image URLs
const getHeroImageUrl = (imageUrl) => {
  if (!imageUrl) return imageUrl;
  if (imageUrl.includes('/card/') && imageUrl.includes('_250x200')) {
    return imageUrl
      .replace('/card/', '/hero/')
      .replace('_250x200', '_500xauto');
  }
  return imageUrl;
};

export default function EnrollmentModals({ isOpen, onClose, course, section, onEnrollmentSuccess, allCourseEnrolledLearnerIds }) {
  const [showEnrollModal, setShowEnrollModal] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [learners, setLearners] = useState([]);
  const [selectedLearnerId, setSelectedLearnerId] = useState('');
  const [paymentOption, setPaymentOption] = useState('upfront');
  const [enrolling, setEnrolling] = useState(false);

  // ClassWallet state
  const [classwalletEnabled, setClasswalletEnabled] = useState(false);
  const [classwalletLoading, setClasswalletLoading] = useState(false);
  const [classwalletError, setClasswalletError] = useState('');

  // Coupon state management
  const [couponCode, setCouponCode] = useState('');
  const [couponValidation, setCouponValidation] = useState(null);
  // Shape: { valid: true, effectiveDiscount: 10, cappedAt70: false, message: '...', promoCodeId, couponId, originalPrice, finalPrice }
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  // Fetch learners function that returns the data (for onRefreshLearners)
  const fetchLearners = useCallback(async () => {
    try {
      const res = await fetch('/api/learners', { credentials: 'include' });
      const data = res.ok ? await res.json() : [];
      setLearners(data || []);
      return data || [];
    } catch (err) {
      console.error(err);
      return [];
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setShowEnrollModal(true);
      setShowPaymentModal(false);
      setClasswalletError('');

      fetchLearners().then(data => {
        if (data.length) setSelectedLearnerId(data[0].id.toString());
      });

      // Fetch ClassWallet enabled status
      fetch('/api/platform-settings')
        .then(res => res.json())
        .then(data => setClasswalletEnabled(!!data.classwalletEnabled))
        .catch(() => setClasswalletEnabled(false));
    }
  }, [isOpen, fetchLearners]);

  // NOTE: Coupon validation is preserved when payment option changes.
  // EnrollModal calculates the effective discount for each payment type locally using rawDiscount.

  const formatDate = dateString =>
    new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const handleNext = () => {
    setShowEnrollModal(false);
    setShowPaymentModal(true);
  };

  const handleGoBack = () => {
    setShowPaymentModal(false);
    setShowEnrollModal(true);
  };

  const isFreeTrial = course?.firstClassFree === true;

  // Free trial enrollment handler - calls API directly, skipping Stripe
  const handleFreeTrialEnroll = async () => {
    if (!selectedLearnerId || !section?.id) return;

    setEnrolling(true);
    try {
      const response = await fetch('/api/enrollment/free-trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sectionId: section.id,
          learnerId: selectedLearnerId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to enroll');
      }

      // Success - refresh sections and close modal
      onEnrollmentSuccess?.();
      onClose();
    } catch (err) {
      alert(err.message || 'Failed to enroll in free trial.');
    } finally {
      setEnrolling(false);
    }
  };

  // Handler for learner change - receives string ID directly from EnrollModal
  const handleLearnerChange = (learnerId) => {
    setSelectedLearnerId(learnerId);
  };

  // Coupon validation handler
  const handleValidateCoupon = async (code) => {
    if (!code.trim()) {
      setCouponValidation(null);
      setCouponError('');
      return;
    }

    setCouponLoading(true);
    setCouponError('');

    try {
      const response = await fetch('/api/stripe/validate-coupon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          couponCode: code,
          sectionId: section.id,
          paymentType: paymentOption === 'upfront' ? 'ONE_TIME' : 'SUBSCRIPTION'
        }),
      });

      const data = await response.json();

      if (data.valid) {
        setCouponValidation(data);
        setCouponError('');
      } else {
        setCouponValidation(null);
        setCouponError(data.error || 'Invalid coupon');
      }
    } catch (error) {
      setCouponValidation(null);
      setCouponError('Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  // Clear coupon handler
  const handleClearCoupon = () => {
    setCouponCode('');
    setCouponValidation(null);
    setCouponError('');
  };

  // ClassWallet checkout handler
  const handleClassWalletCheckout = async () => {
    if (!selectedLearnerId || !section?.id) return;

    setClasswalletLoading(true);
    setClasswalletError('');

    try {
      const response = await fetch('/api/classwallet/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sectionId: section.id,
          learnerId: selectedLearnerId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create ClassWallet order');
      }

      // Redirect to ClassWallet checkout
      window.location.href = data.checkoutUrl;
    } catch (err) {
      setClasswalletError(err.message || 'Failed to start ClassWallet checkout.');
      setClasswalletLoading(false);
    }
  };

  if (!isOpen || !course || !section) return null;

  // Use all-course enrolled IDs when available, fallback to section-only
  const enrolledLearnerIds = allCourseEnrolledLearnerIds?.length > 0
    ? allCourseEnrolledLearnerIds
    : (section?.enrolledLearners?.map(l => l.id) || []);

  const courseInfo = {
    image: getHeroImageUrl(course.imageUrl) || '/images/class-image.png',
    name: course.name || 'Untitled Course',
    dates: `${formatDate(section.startDate)} – ${formatDate(section.endDate)}`
  };

  // Build sidebarContent from course.sidebar
  const sidebarContent = course.sidebar || {
    totalPrice: course.price || 0,
    weeklyPrice: 0,
    courseDuration: 0
  };

  return (
    <>
      {showEnrollModal && (
        <EnrollModal
          courseImage={courseInfo.image}
          courseName={courseInfo.name}
          dateRange={courseInfo.dates}
          sidebarContent={sidebarContent}
          learners={learners}
          selectedLearnerId={selectedLearnerId}
          enrolledLearnerIds={enrolledLearnerIds}
          paymentOption={paymentOption}
          onClose={onClose}
          onLearnerChange={handleLearnerChange}
          onPaymentChange={setPaymentOption}
          onNext={handleNext}
          onRefreshLearners={fetchLearners}
          course={course}
          // Free trial props
          isFreeTrial={isFreeTrial}
          onFreeTrial={handleFreeTrialEnroll}
          enrolling={enrolling}
          // Coupon props
          couponCode={couponCode}
          setCouponCode={setCouponCode}
          couponValidation={couponValidation}
          couponLoading={couponLoading}
          couponError={couponError}
          onValidateCoupon={handleValidateCoupon}
          onClearCoupon={handleClearCoupon}
          // ClassWallet props
          classwalletEnabled={classwalletEnabled}
          onClassWalletCheckout={handleClassWalletCheckout}
          classwalletLoading={classwalletLoading}
          classwalletError={classwalletError}
        />
      )}

      {showPaymentModal && (
        <PaymentModal
          courseImage={courseInfo.image}
          courseName={courseInfo.name}
          dateRange={courseInfo.dates}
          onClose={onClose}
          onGoBack={handleGoBack}
          sectionId={section.id}
          selectedLearner={learners.find(l => l.id.toString() === selectedLearnerId)}
          coursePrice={course.sidebar?.totalPrice || course.price}
          weeklyPrice={course.sidebar?.weeklyPrice}
          paymentType={paymentOption === 'upfront' ? 'ONE_TIME' : 'SUBSCRIPTION'}
          // Coupon props
          couponCode={couponCode}
          couponValidation={couponValidation}
        />
      )}
    </>
  );
}
